import React from "react";

import TarotMessage, { parseTarotMessage } from "../../TarotMessage";
import WesternChartMessage from "../../WesternChartMessage";
import PalmAnalysisMessage from "../../PalmAnalysisMessage";
import KundaliChartMessage from "./KundaliChartMessage";
import PalmPhotoMessage from "./PalmPhotoMessage";
import TextMessage from "./TextMessage";
import ReadTick from "./ReadTick";

/**
 * Dispatches a chat message to the right presentational component based on
 * its `kind` (or text-pattern, in the case of inline TAROT_DRAW blocks).
 *
 * Layout responsibilities (justify-start vs justify-end + max-width + the
 * read-receipt tick) live here so the leaf components stay pure rendering
 * units. Adding a new message type = create one new component + add a case
 * here.
 */
const MessageRenderer = ({ message, isRead }) => {
  const m = message;
  const isCust = m.sender_role === "customer";

  // 0) System messages — chat-ended notice, follow-up nudges, etc.
  //    Centered pill, no avatar, no read-tick. Distinctive amber tint so
  //    it doesn't read as a chat bubble.
  if (m.kind === "system" || m.sender_role === "system") {
    return (
      <div className="flex justify-center my-2" data-testid={`msg-${m.message_id}`}>
        <div className="max-w-[90%] sm:max-w-[78%] px-4 py-2 rounded-full bg-amber-100 border border-amber-300 text-[#3F2D1B] text-xs sm:text-[13px] leading-snug text-center">
          {m.text}
        </div>
      </div>
    );
  }

  // 1) Astrologer-emitted inline tarot draw (text contains <TAROT_DRAW>)
  if (!isCust && m.text && m.text.includes("<TAROT_DRAW")) {
    const parsed = parseTarotMessage(m.text);
    if (parsed.hasDraw) {
      return (
        <div className="flex justify-start" data-testid={`msg-${m.message_id}`}>
          <TarotMessage text={m.text} />
        </div>
      );
    }
  }

  // 2) Western chart (US psychics)
  if (m.kind === "western_chart" && m.payload?.western) {
    return (
      <div className="flex justify-start" data-testid={`msg-${m.message_id}`}>
        <WesternChartMessage payload={m.payload} text={m.text} />
      </div>
    );
  }

  // 3) Palm analysis (annotated overlay + reading) — Sunita Rao
  if (m.kind === "palm_analysis" && m.payload) {
    return (
      <div className="flex justify-start" data-testid={`msg-${m.message_id}`}>
        <PalmAnalysisMessage payload={m.payload} text={m.text} />
      </div>
    );
  }

  // 4) Customer's uploaded palm photo
  if (m.kind === "palm_photo" && m.payload?.original_url) {
    return (
      <div className="flex justify-end" data-testid={`msg-${m.message_id}`}>
        <PalmPhotoMessage payload={m.payload} />
      </div>
    );
  }

  // 5) Vedic kundali chart
  if (m.kind === "kundali_chart" && m.payload?.kundali) {
    return (
      <div className="flex justify-start" data-testid={`msg-${m.message_id}`}>
        <KundaliChartMessage payload={m.payload} text={m.text} />
      </div>
    );
  }

  // 6) Fallback: plain text bubble with read-tick for customer side
  return (
    <div className={`flex ${isCust ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
      <div className={`flex flex-col ${isCust ? "items-end" : "items-start"} gap-0.5 max-w-[85%] sm:max-w-[78%]`}>
        {/* "After session" pill — only on post-end messages from the
            astrologer so the seeker understands these are follow-up
            notes outside billing, not a continuation of the live chat. */}
        {!isCust && m.post_chat && (
          <span className="text-[9px] uppercase tracking-widest text-amber-700 font-semibold bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5 mb-1" data-testid={`msg-${m.message_id}-aftersession`}>
            After session · follow-up
          </span>
        )}
        <TextMessage text={m.text} isCustomer={isCust} />
        {isCust && <ReadTick read={isRead} />}
      </div>
    </div>
  );
};

export default MessageRenderer;
